import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
    signInWithPopup, 
    signOut, 
    onAuthStateChanged,
    signInAnonymously
} from 'firebase/auth';
import { 
    collection, 
    query, 
    where, 
    getDocs, 
    doc, 
    setDoc 
} from 'firebase/firestore';
import { auth, db, googleProvider, DB_PATH } from '../config/firebase';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [authReady, setAuthReady] = useState(false);

    useEffect(() => {
        if (!auth) {
            setAuthReady(true);
            return;
        }
        try {
            const unsubscribe = onAuthStateChanged(auth, async (user) => {
                if (user) {
                    if (user.isAnonymous) {
                         // Proxy garante que o usuário Preview tenha todas as permissões liberadas automaticamente
                         setCurrentUser({ 
                             id: user.uid, 
                             role: 'SuperAdm', 
                             name: 'Deus (Preview)', 
                             email: 'preview@grupoacad.com.br',
                             permissions: new Proxy({}, { get: () => true }) 
                         });
                         setAuthReady(true);
                    } else {
                         // 1. Busca no Banco de Dados pelo email exato do Google
                         // Adaptado à regra de tenant DB_PATH
                         const q = query(collection(db, DB_PATH + 'users'), where("email", "==", user.email));
                         const querySnapshot = await getDocs(q);

                         if (!querySnapshot.empty) {
                             // O usuário já existe no banco de dados
                             const userData = querySnapshot.docs[0].data();
                             const photoURL = user.photoURL || userData.photoURL || '';
                             setCurrentUser({ id: querySnapshot.docs[0].id, ...userData, photoURL });
                         } else {
                             // O usuário NÃO existe! Levanta a flag "needsRegistration"
                             setCurrentUser({
                                 id: user.uid,
                                 email: user.email,
                                 name: user.displayName || '',
                                 photoURL: user.photoURL || '',
                                 needsRegistration: true // Essa flag bloqueia a entrada e chama a tela do formulário
                             });
                         }
                         setAuthReady(true);
                    }
                } else {
                    setCurrentUser(null);
                    setAuthReady(true);
                }
            });
            return () => unsubscribe();
        } catch (e) {
            console.error("Erro no Auth listener:", e);
            setAuthReady(true);
        }
    }, []);

    // FUNÇÕES DE LOGIN
    const loginWithGoogle = async (forceAccountSelection = false) => {
        try {
            if (forceAccountSelection) {
                googleProvider.setCustomParameters({ prompt: 'select_account' });
            } else {
                googleProvider.setCustomParameters({});
            }
            await signInWithPopup(auth, googleProvider);
        } catch (error) {
            console.error("Erro no login Google:", error);
            alert("Não foi possível fazer o login com o Google.");
        }
    };

    const loginWithGodMode = async () => {
        try {
            // BYPASS: Injetamos o usuário SuperAdm com Proxy de permissões completas
            setCurrentUser({ 
                id: 'deus-123', 
                role: 'SuperAdm', 
                name: 'Deus (Preview)', 
                email: 'preview@grupoacad.com.br',
                permissions: new Proxy({}, { get: () => true })
            });
            setAuthReady(true);
        } catch (error) {
            console.error("Erro no bypass do God Mode:", error);
        }
    };

    const logout = async () => {
        try {
            await signOut(auth);
            setCurrentUser(null);
        } catch (error) {
            console.error("Erro ao fazer logout:", error);
        }
    };

    // FUNÇÃO PARA SALVAR SOLICITAÇÃO NO BANCO
    const submitRegistration = async (formData) => {
        if (!db) return;
        const newUserId = Date.now().toString(); // Cria um ID único
        const newUser = {
            id: newUserId,
            email: currentUser.email,
            name: formData.name,
            departmentId: formData.departmentId,
            role: 'Solicitante',
            status: 'pendente',
            createdAt: Date.now()
        };
        
        // Salva na coleção 'users' do tenant (usando DB_PATH)
        await setDoc(doc(db, DB_PATH + 'users', newUserId), newUser);
        
        // Atualiza o estado para "Acesso Pendente"
        setCurrentUser({ ...newUser });
    };

    return (
        <AuthContext.Provider value={{ 
            currentUser, 
            setCurrentUser,
            authReady, 
            loginWithGoogle, 
            loginWithGodMode, 
            logout, 
            submitRegistration 
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
